// src/managerPortal/pages/EventManagement.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import "../styles/EventManagement.css";
import SidebarMenu from "../components/SidebarMenu";
import { useNavigate } from "react-router-dom";

// Buttons
import AddButton from "../components/common/AddButton";
import EditButton from "../components/common/EditButton";
import DeleteButton from "../components/common/DeleteButton";

// Modals
import EventModal from "../components/modals/EventModal";
import DeleteConfirmModal from "../components/modals/DeleteConfirmModal";

// Import our dynamic API URL
import apiUrl from "../../config";

// Import the Pagination component
import Pagination from "../components/Pagination";

const EventManagement = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc");

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Modals
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("add");
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteTargetEvent, setDeleteTargetEvent] = useState(null);

    // ─────────────────────────────────────────────────────────────────────────────
    // Fetch events (Manager Route)
    // ─────────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/manager/events`, {
                    withCredentials: true,
                });
                setEvents(response.data);
            } catch (error) {
                if (error.response && error.response.status === 403) {
                    navigate("/manager/no-access");
                } else {
                    console.error("Error fetching events:", error);
                }
            }
        };

        fetchEvents();
    }, [navigate]);

    // ─────────────────────────────────────────────────────────────────────────────
    // Open/Close Modals
    // ─────────────────────────────────────────────────────────────────────────────
    const openAddModal = () => {
        setModalMode("add");
        setSelectedEvent(null);
        setIsEventModalOpen(true);
    };

    const openEditModal = (eventData) => {
        setModalMode("edit");
        setSelectedEvent(eventData);
        setIsEventModalOpen(true);
    };

    const openDeleteModal = (eventData) => {
        setDeleteTargetEvent(eventData);
        setIsDeleteModalOpen(true);
    };

    const closeEventModal = () => {
        setIsEventModalOpen(false);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // Add an event (Manager Route)
    // ─────────────────────────────────────────────────────────────────────────────
    const handleAddEvent = async (formData) => {
        try {
            const response = await axios.post(`${apiUrl}/api/manager/events`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
                withCredentials: true,
            });
            const { newEvent } = response.data;
            setEvents((prev) => [...prev, newEvent]);
            closeEventModal();
        } catch (error) {
            console.error("Error adding event:", error);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // Edit an event (Manager Route)
    // ─────────────────────────────────────────────────────────────────────────────
    const handleEditEvent = async (id, formData) => {
        try {
            const response = await axios.put(`${apiUrl}/api/manager/events/${id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
                withCredentials: true,
            });
            const { updatedEvent } = response.data;
            setEvents((prev) => prev.map((ev) => (ev._id === id ? updatedEvent : ev)));
            closeEventModal();
        } catch (error) {
            console.error("Error editing event:", error);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // Delete an event (Manager Route)
    // ─────────────────────────────────────────────────────────────────────────────
    const handleDeleteEvent = async () => {
        if (!deleteTargetEvent) return;
        try {
            await axios.delete(`${apiUrl}/api/manager/events/${deleteTargetEvent._id}`, {
                withCredentials: true,
            });
            setEvents((prev) => prev.filter((e) => e._id !== deleteTargetEvent._id));
            closeDeleteModal();
        } catch (error) {
            console.error("Error deleting event:", error);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // Sorting & Filtering
    // ─────────────────────────────────────────────────────────────────────────────
    const handleSort = (field) => {
        let order = "asc";
        if (sortField === field && sortOrder === "asc") {
            order = "desc";
        }
        setSortField(field);
        setSortOrder(order);
    };

    const filteredEvents = events.filter((event) =>
        event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.date.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const sortedEvents = sortField
        ? [...filteredEvents].sort((a, b) => {
            if (a[sortField] < b[sortField]) return sortOrder === "asc" ? -1 : 1;
            if (a[sortField] > b[sortField]) return sortOrder === "asc" ? 1 : -1;
            return 0;
        })
        : filteredEvents;

    // ─────────────────────────────────────────────────────────────────────────────
    // Pagination
    // ─────────────────────────────────────────────────────────────────────────────
    const totalPages = Math.ceil(sortedEvents.length / itemsPerPage);
    const indexOfLastEvent = currentPage * itemsPerPage;
    const indexOfFirstEvent = indexOfLastEvent - itemsPerPage;
    const currentEvents = sortedEvents.slice(indexOfFirstEvent, indexOfLastEvent);

    const clearSearch = () => {
        setSearchQuery("");
        setCurrentPage(1);
    };

    return (
        <div className="layout">
            <SidebarMenu />
            <div className="main-content">
                <div className="card">
                    <div className="card-header">
                        <h4>Event List</h4>
                    </div>

                    <div className="card-body">
                        {/* Search + Add Button Container */}
                        <div className="search-add-container">
                            <div className="search-wrapper">
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search by title or date..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                                <button className="btn-clear" onClick={clearSearch}>
                                    Clear
                                </button>
                            </div>
                            <AddButton onClick={openAddModal} />
                        </div>

                        {/* Events Table */}
                        <div className="table-responsive">
                            <table className="events-table">
                                <thead>
                                <tr>
                                    <th>Image</th>
                                    <th
                                        className="sortable"
                                        onClick={() => handleSort("title")}
                                    >
                                        Title{" "}
                                        {sortField === "title"
                                            ? sortOrder === "asc"
                                                ? "▲"
                                                : "▼"
                                            : ""}
                                    </th>
                                    <th
                                        className="sortable"
                                        onClick={() => handleSort("date")}
                                    >
                                        Date{" "}
                                        {sortField === "date"
                                            ? sortOrder === "asc"
                                                ? "▲"
                                                : "▼"
                                            : ""}
                                    </th>
                                    <th>Time</th>
                                    <th className="actions-col">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {currentEvents.map((event) => (
                                    <tr key={event._id}>
                                        <td>
                                            <img
                                                src={`${apiUrl}${event.image}`}
                                                alt={event.title}
                                                className="manager-event-image"
                                            />
                                        </td>
                                        <td>{event.title}</td>
                                        <td>{event.date}</td>
                                        <td>{event.time}</td>
                                        <td className="actions-col">
                                            <div className="action-buttons">
                                                <EditButton onClick={() => openEditModal(event)} />
                                                <DeleteButton onClick={() => openDeleteModal(event)} />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Use the Pagination component */}
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setCurrentPage}
                            onItemsPerPageChange={(value) => {
                                setItemsPerPage(value);
                                setCurrentPage(1);
                            }}
                            storageKey="eventsItemsPerPage"  // This key uniquely identifies the setting for this page
                        />
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {isEventModalOpen && (
                <EventModal
                    mode={modalMode}
                    eventData={selectedEvent}
                    onClose={closeEventModal}
                    onAdd={handleAddEvent}
                    onEdit={handleEditEvent}
                />
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <DeleteConfirmModal
                    eventTitle={deleteTargetEvent?.title}
                    onClose={closeDeleteModal}
                    onConfirm={handleDeleteEvent}
                />
            )}
        </div>
    );
};

export default EventManagement;
